import { useState, useEffect, useCallback, useRef } from "react";
import {
  getStuckChunks,
  deleteChunks,
  deleteUpload,
  db,
} from "../idb/chunk-db";
import logger from "../utils/logger";
import { TChunkStatus } from "../types/db";
import { isUserAuthenticated, getCookie } from "../services/user";
import { useUserStore } from "./useUserStore";
import { getAuthToken } from "../utils/authUtils";

interface StuckChunk {
  chunkNo: number;
  status: TChunkStatus;
  size: number;
  createdAt: number;
}

interface StuckChunksData {
  videoGuid: string;
  uploadId?: string;
  key?: string;
  chunks: StuckChunk[];
}

interface UseStuckChunksHandlerResult {
  showOverlay: boolean;
  stuckChunks: StuckChunksData[];
  totalChunks: number;
  isProcessing: boolean;
  onRetry: () => void;
  onDiscard: () => void;
  setIsProcessing: (v: boolean) => void;
}

export const useStuckChunksHandler = ({
  retryUpload,
  isUploading,
  isOnline,
  setShowDownloadOnlyOverlay,
  setIsUploading,
  showDownloadOnlyOverlay,
}: {
  retryUpload: (videoGuid: string, force?: boolean) => Promise<void>;
  isUploading: boolean;
  setShowDownloadOnlyOverlay: (v: boolean) => void;
  isOnline?: boolean;
  setIsUploading: (v: boolean) => void;
  showDownloadOnlyOverlay: boolean;
}): UseStuckChunksHandlerResult => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [stuckChunks, setStuckChunks] = useState<StuckChunksData[]>([]);
  const [totalChunks, setTotalChunks] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get user data and loading state from userStore
  const { userData, loading: userDataLoading } = useUserStore();

  // Function to check for stuck chunks
  const checkStuckChunks = useCallback(async () => {
    try {
      const stuckData = await getStuckChunks();
      if (stuckData.totalChunks > 0) {
        logger.info(
          `Found ${stuckData.totalChunks} stuck chunks from ${stuckData.videoGuids.length} recordings`
        );
        setStuckChunks(stuckData.stuckChunks);
        setTotalChunks(stuckData.totalChunks);

        // Check if user is authenticated
        if (isUserAuthenticated()) {
          setShowOverlay(true);
        } else {
          const hasCookie = !!getCookie("SPAExtensionSSO");
          const hasUserData = !!userData;
          const isUserDataLoading = userDataLoading;

          if (!hasCookie && !hasUserData && !isUserDataLoading) {
            setShowDownloadOnlyOverlay(true);
            setIsUploading(false);
          }
        }
      }
    } catch (error) {
      logger.error("Error checking for stuck chunks:", error);
    }
  }, [userData, userDataLoading]);

  // Check for stuck chunks on mount
  useEffect(() => {
    checkStuckChunks();
  }, [checkStuckChunks]);

  // Track previous online state to prevent infinite loops
  const prevIsOnlineRef = useRef<boolean | undefined>(undefined);

  // Check for stuck chunks when connection comes back online
  useEffect(() => {
    // Only trigger if isOnline changed from false/undefined to true
    if (
      (isOnline === true && prevIsOnlineRef.current !== true && !isUploading) ||
      (showDownloadOnlyOverlay && !isUploading)
    ) {
      //logger.info("Connection restored, checking for stuck chunks...");
      checkStuckChunks();
    }
    if (isUploading && isOnline) {
      //logger.info("Connection restored, checking for stuck chunks 2...");

      setShowOverlay(false);
    }
    // Update the ref to track the current state
    prevIsOnlineRef.current = isOnline;
  }, [isOnline, checkStuckChunks, isUploading, showDownloadOnlyOverlay]);

  const onRetry = useCallback(async () => {
    const stuckData = await getStuckChunks();
    if (stuckChunks.length === 0 && stuckData.totalChunks <= 0) return;
    setIsUploading(false);

    setIsProcessing(true);

    try {
      logger.info("Retrying upload for stuck chunks");

      // Retry upload for each videoGuid
      for (const recording of stuckChunks) {
        try {
          await retryUpload(recording.videoGuid, true);
          logger.info(`Retry initiated for videoGuid: ${recording.videoGuid}`);
        } catch (error) {
          logger.error(
            `Failed to retry upload for ${recording.videoGuid}:`,
            error
          );
        }
      }

      setShowOverlay(false);
      setShowDownloadOnlyOverlay(false);
    } catch (error) {
      logger.error("Error during retry process:", error);
    } finally {
    }
  }, [stuckChunks, retryUpload]);

  const onDiscard = useCallback(async () => {
    const stuckData = await getStuckChunks();
    if (stuckChunks.length === 0 && stuckData.totalChunks <= 0) return;
    setIsUploading(false);

    setIsProcessing(true);
    const _chunks = stuckChunks.length ? stuckChunks : stuckData.stuckChunks;
    try {
      logger.info("Discarding all stuck uploads");

      // Process each stuck chunk group (already grouped by videoGuid)
      for (const recording of _chunks) {
        try {
          const { videoGuid, uploadId, key } = recording;

          // Call abort API if we have uploadId and key
          if (uploadId && key) {
            try {
              const authToken = getAuthToken();
              const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/Transcoder/abort-multipart-upload`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                  body: JSON.stringify({
                    uploadId,
                    key,
                    videoGuid,
                  }),
                }
              );

              if (response.ok) {
                logger.info(`Abort API called for videoGuid: ${videoGuid}`);
              } else {
                logger.warn(`Abort API failed for videoGuid: ${videoGuid}`);
              }
            } catch (apiError) {
              logger.error(
                `Failed to call abort API for ${videoGuid}:`,
                apiError
              );
            }
          } else {
            logger.warn(
              `No uploadId/key found for videoGuid: ${videoGuid}, skipping abort API call`
            );
          }
          // Delete chunks from database
          await deleteChunks(videoGuid);

          // Delete upload record
          await deleteUpload(videoGuid);
          setShowDownloadOnlyOverlay(false);
          logger.info(`Discarded upload for videoGuid: ${videoGuid}`);
        } catch (error) {
          logger.error(
            `Failed to discard upload for ${recording.videoGuid}:`,
            error
          );
        }
      }

      setShowOverlay(false);
    } catch (error) {
      logger.error("Error during discard process:", error);
    } finally {
    }
  }, [stuckChunks]);

  return {
    showOverlay,
    stuckChunks,
    totalChunks,
    isProcessing,
    setIsProcessing,
    onRetry,
    onDiscard,
  };
};
